import _ from 'lodash';
import { useMemo } from 'react';
import {
  Params,
  Sort,
  listItemsFactory,
  useAutocompleteSource,
  useEventCallback,
  useFetch,
  useScrollableSource,
} from '@eas/common-web';
import { DictionaryAutocomplete, DictionaryObject } from '@models';
import { EvidenceApiUrl } from '@enums';

export function useSubjects() {
  return useAutocompleteSource<DictionaryAutocomplete>({
    url: `${EvidenceApiUrl.SUBJECTS}/autocomplete`,
    apiUrl: EvidenceApiUrl.SUBJECTS,
  });
}

/**
 * List all available subject types.
 */
export function useTypes() {
  return useFetch<DictionaryObject[]>(`${EvidenceApiUrl.SUBJECTS}/type/list`, {
    method: 'GET',
    cacheResponse: true,
  });
}

/**
 * List all available subject sexes.
 */
export function useSexes() {
  return useFetch<DictionaryObject[]>(`${EvidenceApiUrl.SUBJECTS}/sex/list`, {
    method: 'GET',
    cacheResponse: true,
  });
}

/**
 * List all available isds subject types.
 */
export function useIsdsSubjectTypes() {
  return useFetch<DictionaryObject[]>(
    `${EvidenceApiUrl.SUBJECTS}/isds/type/list`,
    {
      method: 'GET',
      cacheResponse: true,
    }
  );
}

/**
 * List all available data box types.
 */
export function useDataBoxTypes() {
  return useFetch<DictionaryObject[]>(
    `${EvidenceApiUrl.SUBJECTS}/data-box/type/list`,
    {
      method: 'GET',
      cacheResponse: true,
    }
  );
}

export const CONTACTS_SORT = [
  {
    field: 'subject.label',
    type: 'FIELD',
    order: 'ASC',
  },
  {
    field: 'subject.id',
    type: 'FIELD',
    order: 'ASC',
  },
  {
    field: 'label',
    type: 'FIELD',
    order: 'ASC',
  },
] as Sort[];

export function useContacts(params?: Params) {
  return useAutocompleteSource<DictionaryAutocomplete>({
    url: `${EvidenceApiUrl.SUBJECT_CONTACTS}/autocomplete`,
    apiUrl: EvidenceApiUrl.SUBJECT_CONTACTS,
    params: params ?? {
      sort: CONTACTS_SORT,
    },
  });
}

export function useMergedJudgeSolvers() {
  const url = `${EvidenceApiUrl.ES_FILES}/autocomplete/merged`;
  const source = useScrollableSource<DictionaryAutocomplete>({
    url,
    params: {
      size: 50,
    },
    listItems: listItemsFactory({
      postProcess: async (data: any) => {
        if (Array.isArray(data)) {
          return { items: _.sortBy(data, 'label'), count: data.length };
        }
        return {
          items: [],
          count: 0,
        } as any;
      },
    }),
  });
  // 3. Emulate search query handler
  const setSearchQuery = useEventCallback((q: string) => {
    source.setUrl(`${url}?query=${encodeURIComponent(q)}`);
  });
  return useMemo(
    () => ({
      ...source,
      setSearchQuery,
      loadDetail: async (item: DictionaryAutocomplete) => {
        return item;
      },
    }),
    [source, setSearchQuery]
  );
}
